/**
 * Gallery state & handlers for the director page lightbox.
 *
 * Owns: image list, selection, keyboard nav, download/share/animate/delete.
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useImageDelete } from "@/hooks/useImageDelete";
import { useGeneratedImages } from "@/hooks/useGeneratedImages";
import { imageToDisplayMedia } from "@/lib/types/media";
import type { DisplayMedia } from "@/lib/types/media";
import { useLightboxNavigation } from "@/hooks/useLightboxNavigation";
import { downloadMedia, shareMedia } from "@/lib/mediaUtils";

export function useDirectorGallery() {
  const navigate = useNavigate();
  const { deleteImageByUrl, deleteFailedImages } = useImageDelete();

  // ── Image list ──────────────────────────────

  const { images: dbDirectorImages, pendingImages: directorPendingImages, pendingCount: directorPendingCount, completingIds: directorCompletingIds, failedImages: directorFailedImages, loading: directorLoading, refetch: refetchDirectorImages } = useGeneratedImages(undefined, "director");

  const directorImages: DisplayMedia[] = useMemo(
    () => dbDirectorImages.map(imageToDisplayMedia),
    [dbDirectorImages],
  );

  // ── Selection state ─────────────────────────

  const [selectedImage, setSelectedImage] = useState<DisplayMedia | null>(null);

  const { currentMediaIndex: selectedImageIndex, canNavigate, goToNextItem, goToPrevItem } =
    useLightboxNavigation(directorImages, selectedImage, setSelectedImage);

  // ── Action handlers ─────────────────────────

  const handleDeleteImage = async (imageUrl: string) => {
    const success = await deleteImageByUrl(imageUrl);
    if (success) {
      refetchDirectorImages();
      setSelectedImage(null);
    }
  };

  const handleDownloadImage = () => {
    if (selectedImage) {
      downloadMedia(selectedImage.url, "todoai-director");
    }
  };

  const handleShareImage = () => {
    if (!selectedImage?.id) return;
    void shareMedia({ id: selectedImage.id, type: "image" });
  };

  const handleAnimateImage = () => {
    if (!selectedImage) return;
    navigate("/frames-to-video", { state: { animateImage: selectedImage.url } });
    setSelectedImage(null);
  };

  const handleClearFailed = () => {
    if (directorFailedImages.length > 0) {
      deleteFailedImages(directorFailedImages.map((f) => f.id));
    }
  };

  // ── Return ──────────────────────────────────

  return {
    directorImages,
    directorPendingCount,
    directorPendingImages,
    directorCompletingIds,
    directorLoading,
    directorFailedImages,
    handleClearFailed,
    refetchDirectorImages,
    selectedImage,
    setSelectedImage,
    selectedImageIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,
    handleDeleteImage,
    handleDownloadImage,
    handleShareImage,
    handleAnimateImage,
  };
}
